import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ratnatraya_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ratnatraya_token');
      localStorage.removeItem('ratnatraya_admin');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Clients API
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Documents API
export const documentsAPI = {
  getByClient: (clientId) => api.get(`/documents/client/${clientId}`),
  getOne: (id) => api.get(`/documents/${id}`),
  upload: (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getAll: () => api.get('/contact'),
  markRead: (id) => api.put(`/contact/${id}/read`),
};

// Blog API
export const blogAPI = {
  getAll: () => api.get('/blog'),
  getOne: (slug) => api.get(`/blog/${slug}`),
  create: (data) => api.post('/blog', data),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: () => api.get('/testimonials'),
  create: (data) => api.post('/testimonials', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// CCFS Leads API
export const ccfsLeadsAPI = {
  getAll: (includeTest = false) => api.get(`/ccfs-leads?include_test=${includeTest}`),
  update: (id, data) => api.patch(`/ccfs-leads/${id}`, data),
  delete: (id) => api.delete(`/ccfs-leads/${id}`),
  deleteAll: () => api.delete('/ccfs-leads?confirm=true'),
  updatePipelineStage: (id, stage) => api.patch(`/ccfs-leads/${id}/pipeline-stage`, { pipeline_stage: stage }),
  uploadMCA: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/ccfs-leads/${id}/upload-mca`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadMCA: (id) => api.get(`/ccfs-leads/${id}/download-mca`, { responseType: 'blob' }),
};

// Analytics API
export const analyticsAPI = {
  trackEvent: (event, metadata = {}, session_id = null) =>
    api.post('/analytics/event', { event, metadata, session_id }).catch(() => {}),
  getFunnel: () => api.get('/analytics/funnel'),
  getSoftLeads: () => api.get('/soft-leads'),
};

// Soft Leads API
export const softLeadsAPI = {
  create: (data) => api.post('/soft-leads', data),
};

// Cases API
export const casesAPI = {
  getAll: () => api.get('/admin/cases'),
  get: (id) => api.get(`/admin/cases/${id}`),
  create: (data) => api.post('/admin/cases', data),
  createFromLead: (leadId) => api.post(`/admin/cases/from-lead/${leadId}`),
  update: (id, data) => api.patch(`/admin/cases/${id}`, data),
  addNote: (id, text) => api.post(`/admin/cases/${id}/notes`, { text }),
  delete: (id) => api.delete(`/admin/cases/${id}`),
};

// Partners API (for admin)
export const partnersAPI = {
  getAll: () => api.get('/admin/partners'),
};

// Partner self-service API (uses rpace_token)
const partnerApi = axios.create({ baseURL: BACKEND_URL + '/api' });
partnerApi.interceptors.request.use(config => {
  const token = localStorage.getItem('rpace_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const partnerCasesAPI = {
  getMyCases: () => partnerApi.get('/partners/cases'),
  getCase: (id) => partnerApi.get(`/partners/cases/${id}`),
  updateTask: (caseId, taskIndex, status) => partnerApi.patch(`/partners/cases/${caseId}/task`, { task_index: taskIndex, status }),
  updateDocument: (caseId, docIndex, status) => partnerApi.patch(`/partners/cases/${caseId}/document`, { doc_index: docIndex, status }),
  executeAction: (caseId, action) => partnerApi.post(`/partners/cases/${caseId}/action`, { action }),
  getMe: () => partnerApi.get('/partners/me'),
  getAvailableLeads: () => partnerApi.get('/partners/available-leads'),
  claimLead: (leadId) => partnerApi.post(`/partners/available-leads/${leadId}/claim`),
};

export default api;
